// frontend/src/pages/AdminWriterReviews.js – Admin-only Founding Writer Reviews
// Build: 2026-05-07-writer-review-decision-justification-v1
// Internal management tool only. No public writer-facing scores.
// Standing principle: Algorithm recommends. Admin decides. Notes explain.

import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import { api } from "../api";
import { useUser } from "../UserContext";

const SCORE_KEYS = [
  ["consistency", "Consistency"],
  ["readerInterest", "Reader interest"],
  ["storyQuality", "Story quality"],
  ["originality", "Originality"],
  ["progress", "Progress"],
  ["communication", "Communication"],
];

const OUTCOMES = [
  ["continue_promotion", "Continue promotion"],
  ["increase_promotion", "Increase promotion"],
  ["extend_review", "Extend review"],
  ["pause_promotion", "Pause promotion"],
  ["reward_candidate", "Reward candidate"],
  ["remove_from_pilot", "Remove from pilot consideration"],
];

const VISIBILITY_DECISIONS = [
  ["not_ready", "Not ready"],
  ["observe", "Continue observing"],
  ["pilot_visible", "Pilot visible"],
  ["increase_visibility", "Increase visibility"],
  ["pause_visibility", "Pause visibility"],
];

const REWARD_CONSIDERATIONS = [
  ["not_considered", "Not considered"],
  ["consider_later", "Consider later"],
  ["eligible_for_review", "Eligible for review"],
  ["approved", "Approved"],
  ["not_approved", "Not approved"],
];

const REWARD_TYPES = [
  ["none", "None"],
  ["recognition_only", "Recognition only"],
  ["platform_credit", "Platform credit"],
  ["financial_reward", "Financial reward"],
  ["other", "Other"],
];

function safeStr(v) {
  return typeof v === "string" ? v : String(v || "");
}

function todayInputValue() {
  try {
    return new Date().toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return d.toLocaleDateString();
  }
}

function outcomeLabel(value) {
  const found = OUTCOMES.find(([key]) => key === value);
  return found ? found[1] : "Extend review";
}

function outcomeClass(value) {
  const v = safeStr(value);
  if (v === "increase_promotion" || v === "reward_candidate") return "good";
  if (v === "pause_promotion" || v === "remove_from_pilot") return "warn";
  if (v === "continue_promotion") return "steady";
  return "neutral";
}

function choiceLabel(list, value, fallback = "—") {
  const found = list.find(([key]) => key === value);
  return found ? found[1] : fallback;
}

function decisionBadgeClass(value) {
  const v = safeStr(value);
  if (v === "increase_visibility" || v === "approved") return "good";
  if (v === "pause_visibility" || v === "not_ready" || v === "not_approved") return "warn";
  if (v === "pilot_visible" || v === "eligible_for_review") return "steady";
  return "neutral";
}

function needsDecisionNote({ visibilityDecision, rewardConsideration, rewardType, decisionJustificationNote, suggestion }) {
  const note = safeStr(decisionJustificationNote).trim();
  const minimumMet = suggestion?.minimumDataMet === true;
  const required =
    visibilityDecision === "increase_visibility" ||
    rewardType === "financial_reward" ||
    (rewardConsideration === "approved" && !minimumMet);
  return required && !note;
}

function blankScores() {
  return {
    consistency: 3,
    readerInterest: 3,
    storyQuality: 3,
    originality: 3,
    progress: 3,
    communication: 3,
  };
}

function scoreOptions() {
  return [1, 2, 3, 4, 5].map((n) => (
    <option value={n} key={n}>
      {n}
    </option>
  ));
}

function weightedScore(scores) {
  const s = scores || {};
  const total =
    Number(s.consistency || 0) * 0.2 +
    Number(s.readerInterest || 0) * 0.25 +
    Number(s.storyQuality || 0) * 0.25 +
    Number(s.originality || 0) * 0.15 +
    Number(s.progress || 0) * 0.1 +
    Number(s.communication || 0) * 0.05;
  return Math.round(total * 100) / 100;
}

function adminScore100(scores) {
  return Math.round(weightedScore(scores) * 20);
}

function confidenceLabel(value) {
  const v = safeStr(value).toLowerCase();
  if (v === "high") return "High confidence";
  if (v === "medium") return "Medium confidence";
  if (v === "low") return "Low confidence";
  return "Insufficient data";
}

function confidenceClass(value) {
  const v = safeStr(value).toLowerCase();
  if (v === "high") return "good";
  if (v === "medium") return "steady";
  if (v === "low") return "warn";
  return "neutral";
}

function suggestionFrom(item) {
  return item?.platformSuggestion || item?.platformSignalSnapshot || null;
}

function scoreDiff(adminScore, suggestion) {
  if (!suggestion || safeStr(suggestion.confidence).toLowerCase() === "insufficient") return 0;
  return Math.abs(Number(adminScore || 0) - Number(suggestion.score || 0));
}

function needsOverrideNote(adminScore, suggestion, note) {
  return scoreDiff(adminScore, suggestion) >= 15 && !safeStr(note).trim();
}

function smallNumber(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n);
}

function PlatformSuggestionCard({ suggestion, adminFinalScore, overrideNote, compact = false }) {
  if (!suggestion) {
    return (
      <div className="admin-review-platform-card admin-review-platform-card-compact">
        <div className="admin-review-platform-card-title">Platform suggestion</div>
        <div className="admin-review-platform-empty">No signal summary yet.</div>
      </div>
    );
  }

  const signals = suggestion.signals || {};
  const breakdown = suggestion.breakdown || {};
  const diff = scoreDiff(adminFinalScore, suggestion);
  const overrideNeeded = needsOverrideNote(adminFinalScore, suggestion, overrideNote);
  const confidence = confidenceLabel(suggestion.confidence);
  const minimumLabel = suggestion.minimumDataMet ? "Minimum data met" : "Minimum data not met";
  const overrideLabel = diff >= 15 ? (overrideNeeded ? "Override note needed" : "Override note saved") : "No override needed";

  return (
    <div className={`admin-review-platform-card ${compact ? "admin-review-platform-card-compact" : ""}`}>
      <div className="admin-review-platform-head">
        <div>
          <div className="admin-review-platform-kicker">Platform suggestion</div>
          <div className="admin-review-platform-principle">Algorithm recommends. Admin decides.</div>
        </div>
        <div className="admin-review-platform-score-row">
          <div className="admin-review-platform-mini-score">
            <span>Admin</span>
            <strong>{smallNumber(adminFinalScore)}</strong>
          </div>
          <div className="admin-review-platform-score">
            <span>{smallNumber(suggestion.score)}</span>
            <small>/100</small>
          </div>
        </div>
      </div>

      <div className="admin-review-platform-badges">
        <span className={`admin-review-outcome ${confidenceClass(suggestion.confidence)}`}>{confidence}</span>
        <span className={`admin-review-outcome ${suggestion.minimumDataMet ? "good" : "warn"}`}>{minimumLabel}</span>
        <span className={`admin-review-outcome ${overrideNeeded ? "warn" : "steady"}`}>{overrideLabel}</span>
      </div>

      {suggestion.rewardCaution ? <p className="admin-review-platform-caution compact">{suggestion.rewardCaution}</p> : null}

      {diff >= 15 ? (
        <p className={overrideNeeded ? "admin-review-platform-warning compact" : "admin-review-platform-caution compact"}>
          Admin score differs by {diff} points. {overrideNeeded ? "Add a brief reason." : "Reason saved."}
        </p>
      ) : null}

      <details className="admin-review-platform-details">
        <summary>View signal details</summary>

        <div className="admin-review-platform-grid">
          <div>
            <strong>{smallNumber(signals.totalContributions)}</strong>
            <span>Contributions</span>
          </div>
          <div>
            <strong>{smallNumber(signals.adoptedContributions)}</strong>
            <span>Adopted/canon</span>
          </div>
          <div>
            <strong>{smallNumber(signals.pendingProposalCount)}</strong>
            <span>Pending</span>
          </div>
          <div>
            <strong>{smallNumber(signals.activeDays)}</strong>
            <span>Active days</span>
          </div>
        </div>

        <div className="admin-review-platform-breakdown">
          <span>Activity {smallNumber(breakdown.activityScore)}</span>
          <span>Adoption {smallNumber(breakdown.adoptionScore)}</span>
          <span>Consistency {smallNumber(breakdown.consistencyScore)}</span>
          <span>Reader signals {smallNumber(breakdown.readerSignalScore)}</span>
        </div>

        {Array.isArray(suggestion.explanation) && suggestion.explanation.length ? (
          <ul className="admin-review-platform-notes">
            {suggestion.explanation.slice(0, 4).map((line, idx) => (
              <li key={`${line}-${idx}`}>{line}</li>
            ))}
          </ul>
        ) : null}
      </details>
    </div>
  );
}

function DecisionJustificationCard({
  visibilityDecision,
  setVisibilityDecision,
  rewardConsideration,
  setRewardConsideration,
  rewardType,
  setRewardType,
  rewardAmountText,
  setRewardAmountText,
  decisionJustificationNote,
  setDecisionJustificationNote,
  decisionNoteRequired,
}) {
  return (
    <details className="admin-review-decision-card">
      <summary>Decision & Reward Justification</summary>
      <div className="admin-review-decision-body">
        <p className="admin-review-decision-note">
          Internal only. No payment is made here, and nothing is shown to writers or the public.
        </p>

        <div className="admin-review-form-grid admin-review-decision-grid">
          <label>
            Visibility decision
            <select
              className="ns-input"
              value={visibilityDecision}
              onChange={(e) => setVisibilityDecision(e.target.value)}
            >
              {VISIBILITY_DECISIONS.map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Reward consideration
            <select
              className="ns-input"
              value={rewardConsideration}
              onChange={(e) => setRewardConsideration(e.target.value)}
            >
              {REWARD_CONSIDERATIONS.map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Reward type
            <select className="ns-input" value={rewardType} onChange={(e) => setRewardType(e.target.value)}>
              {REWARD_TYPES.map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Reward amount / value
            <input
              className="ns-input"
              value={rewardAmountText}
              onChange={(e) => setRewardAmountText(e.target.value)}
              placeholder="Optional. Example: Recognition only, ₦50,000, $100, platform credit."
            />
          </label>
        </div>

        <label className="admin-review-notes-label">
          Decision note
          <textarea
            className={`admin-review-notes ${decisionNoteRequired ? "needs-note" : ""}`}
            value={decisionJustificationNote}
            onChange={(e) => setDecisionJustificationNote(e.target.value)}
            placeholder="Briefly justify visibility or reward decisions. Required for increased visibility, financial reward, or early reward approval."
          />
        </label>
      </div>
    </details>
  );
}

async function apiGet(path) {
  if (api && typeof api.get === "function") return await api.get(path);
  return await api(path, { method: "GET" });
}

async function apiPost(path, body) {
  if (api && typeof api.post === "function") return await api.post(path, body);
  return await api(path, { method: "POST", body: JSON.stringify(body) });
}

async function apiPatch(path, body) {
  if (api && typeof api.patch === "function") return await api.patch(path, body);
  return await api(path, { method: "PATCH", body: JSON.stringify(body) });
}

async function apiDelete(path) {
  if (api && typeof api.del === "function") return await api.del(path);
  if (api && typeof api.delete === "function") return await api.delete(path);
  return await api(path, { method: "DELETE" });
}

export default function AdminWriterReviews({ onNavigate, onGo }) {
  const { user } = useUser();
  const isAdmin = safeStr(user?.role).toLowerCase() === "admin";

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [stories, setStories] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedStoryId, setSelectedStoryId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const [reviewPeriodLabel, setReviewPeriodLabel] = useState("First review");
  const [reviewDate, setReviewDate] = useState(todayInputValue());
  const [nextReviewDate, setNextReviewDate] = useState("");
  const [outcome, setOutcome] = useState("extend_review");
  const [adminNotes, setAdminNotes] = useState("");
  const [adminOverrideNote, setAdminOverrideNote] = useState("");
  const [visibilityDecision, setVisibilityDecision] = useState("observe");
  const [rewardConsideration, setRewardConsideration] = useState("not_considered");
  const [rewardType, setRewardType] = useState("none");
  const [rewardAmountText, setRewardAmountText] = useState("");
  const [decisionJustificationNote, setDecisionJustificationNote] = useState("");
  const [scores, setScores] = useState(blankScores());

  const selectedStory = useMemo(() => {
    return stories.find((story) => safeStr(story?.id) === safeStr(selectedStoryId)) || null;
  }, [stories, selectedStoryId]);

  const selectedReviews = useMemo(() => {
    if (!selectedStoryId) return reviews;
    return reviews.filter((review) => safeStr(review?.storyId) === safeStr(selectedStoryId));
  }, [reviews, selectedStoryId]);

  const editingReview = useMemo(() => {
    if (!editingId) return null;
    return reviews.find((review) => safeStr(review?.id) === safeStr(editingId)) || null;
  }, [reviews, editingId]);

  const activePlatformSuggestion = useMemo(() => {
    return suggestionFrom(editingReview) || suggestionFrom(selectedStory) || null;
  }, [editingReview, selectedStory]);

  const currentAdminScore100 = adminScore100(scores);
  const overrideRequired = needsOverrideNote(currentAdminScore100, activePlatformSuggestion, adminOverrideNote);
  const decisionNoteRequired = needsDecisionNote({
    visibilityDecision,
    rewardConsideration,
    rewardType,
    decisionJustificationNote,
    suggestion: activePlatformSuggestion,
  });

  function clearFlash() {
    setErr("");
    setOk("");
  }

  function resetForm(storyId = selectedStoryId) {
    setEditingId("");
    setSelectedStoryId(storyId || "");
    setReviewPeriodLabel("First review");
    setReviewDate(todayInputValue());
    setNextReviewDate("");
    setOutcome("extend_review");
    setAdminNotes("");
    setAdminOverrideNote("");
    setVisibilityDecision("observe");
    setRewardConsideration("not_considered");
    setRewardType("none");
    setRewardAmountText("");
    setDecisionJustificationNote("");
    setScores(blankScores());
  }

  async function loadContext(q = query) {
    const qs = q ? `?query=${encodeURIComponent(q)}&limit=150` : "?limit=150";
    const data = await apiGet(`/api/admin/writer-reviews/context${qs}`);
    const rows = Array.isArray(data?.stories) ? data.stories : [];
    setStories(rows);
    if (!selectedStoryId && rows[0]?.id) setSelectedStoryId(rows[0].id);
    return rows;
  }

  async function loadReviews(storyId = selectedStoryId) {
    const qs = storyId ? `?storyId=${encodeURIComponent(storyId)}&limit=100` : "?limit=100";
    const data = await apiGet(`/api/admin/writer-reviews${qs}`);
    setReviews(Array.isArray(data?.reviews) ? data.reviews : []);
  }

  async function loadAll() {
    if (!isAdmin) return;
    clearFlash();
    setLoading(true);
    try {
      const rows = await loadContext(query);
      const storyId = selectedStoryId || rows?.[0]?.id || "";
      await loadReviews(storyId);
    } catch (e) {
      setErr(safeStr(e?.message) || "Failed to load writer reviews.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  async function searchStories(e) {
    e.preventDefault();
    clearFlash();
    setBusy(true);
    try {
      await loadContext(query);
    } catch (ex) {
      setErr(safeStr(ex?.message) || "Search failed.");
    } finally {
      setBusy(false);
    }
  }

  async function changeStory(storyId) {
    clearFlash();
    setSelectedStoryId(storyId);
    resetForm(storyId);
    setBusy(true);
    try {
      await loadReviews(storyId);
    } catch (ex) {
      setErr(safeStr(ex?.message) || "Failed to load story reviews.");
    } finally {
      setBusy(false);
    }
  }

  function editReview(review) {
    if (!review?.id) return;
    setEditingId(review.id);
    setSelectedStoryId(safeStr(review.storyId));
    setReviewPeriodLabel(safeStr(review.reviewPeriodLabel) || "Review");
    setReviewDate(review.reviewDate ? String(review.reviewDate).slice(0, 10) : todayInputValue());
    setNextReviewDate(review.nextReviewDate ? String(review.nextReviewDate).slice(0, 10) : "");
    setOutcome(safeStr(review.outcome) || "extend_review");
    setAdminNotes(safeStr(review.adminNotes));
    setAdminOverrideNote(safeStr(review.adminOverrideNote));
    setVisibilityDecision(safeStr(review.visibilityDecision) || "observe");
    setRewardConsideration(safeStr(review.rewardConsideration) || "not_considered");
    setRewardType(safeStr(review.rewardType) || "none");
    setRewardAmountText(safeStr(review.rewardAmountText));
    setDecisionJustificationNote(safeStr(review.decisionJustificationNote));
    setScores({ ...blankScores(), ...(review.scores || {}) });
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {}
  }

  async function submitReview(e) {
    e.preventDefault();
    clearFlash();

    if (!selectedStoryId) {
      setErr("Please select a storyline before saving a review.");
      return;
    }

    if (overrideRequired) {
      setErr("Please add an admin override note. The final score differs materially from the platform suggested score.");
      return;
    }

    if (decisionNoteRequired) {
      setErr("Please add a decision note for increased visibility, financial reward, or early reward approval.");
      return;
    }

    setBusy(true);
    try {
      const body = {
        storyId: selectedStoryId,
        reviewPeriodLabel,
        reviewDate,
        nextReviewDate: nextReviewDate || null,
        scores,
        outcome,
        adminNotes,
        adminOverrideNote,
        visibilityDecision,
        rewardConsideration,
        rewardType,
        rewardAmountText,
        decisionJustificationNote,
      };

      if (editingId) {
        await apiPatch(`/api/admin/writer-reviews/${encodeURIComponent(editingId)}`, body);
        setOk("Review updated.");
      } else {
        await apiPost("/api/admin/writer-reviews", body);
        setOk("Review saved.");
      }

      resetForm(selectedStoryId);
      await loadReviews(selectedStoryId);
      await loadContext(query);
    } catch (ex) {
      setErr(safeStr(ex?.message) || "Failed to save review.");
    } finally {
      setBusy(false);
    }
  }

  async function deleteReview(review) {
    if (!review?.id) return;
    // eslint-disable-next-line no-alert
    const yes = window.confirm("Delete this internal review? This cannot be undone.");
    if (!yes) return;

    clearFlash();
    setBusy(true);
    try {
      await apiDelete(`/api/admin/writer-reviews/${encodeURIComponent(review.id)}`);
      setOk("Review deleted.");
      await loadReviews(selectedStoryId);
    } catch (ex) {
      setErr(safeStr(ex?.message) || "Failed to delete review.");
    } finally {
      setBusy(false);
    }
  }

  if (!isAdmin) {
    return (
      <div className="ns-page admin-writer-reviews-page">
        <div className="ns-card">
          <h1 className="ns-page-title">Access denied</h1>
          <p className="ns-card-text">This internal review tool is for administrators only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ns-page admin-writer-reviews-page">
      <section className="admin-writer-reviews-hero">
        <div>
          <div className="admin-writer-reviews-kicker">Internal Admin Tool</div>
          <h1>Founding Writers Review Matrix</h1>
          <p>
            Compact internal review for promotion, visibility, reward consideration, and platform-assisted signals.
          </p>
        </div>
        <div className="admin-writer-reviews-actions">
          <button type="button" className="ns-button ns-button-outline" onClick={() => (onNavigate || onGo)?.("admin")}>
            Back to Admin
          </button>
          <button type="button" className="ns-button ns-button-primary" onClick={loadAll} disabled={busy || loading}>
            Refresh
          </button>
        </div>
      </section>

      {err ? <div className="admin-review-flash error">{err}</div> : null}
      {ok ? <div className="admin-review-flash ok">{ok}</div> : null}

      <section className="admin-review-layout">
        <div className="admin-review-panel">
          <h2>Select storyline</h2>
          <form className="admin-review-search" onSubmit={searchStories}>
            <input
              className="ns-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search title, author, or forum"
            />
            <button type="submit" className="ns-button ns-button-outline" disabled={busy}>
              Search
            </button>
          </form>

          <select
            className="admin-review-select"
            value={selectedStoryId}
            onChange={(e) => changeStory(e.target.value)}
            disabled={busy || loading}
          >
            <option value="">Select a storyline</option>
            {stories.map((story) => (
              <option key={story.id} value={story.id}>
                {story.title} {story.authorUsername ? `— @${story.authorUsername}` : ""}
              </option>
            ))}
          </select>

          {selectedStory ? (
            <div className="admin-review-story-card">
              <div className="admin-review-story-title">{selectedStory.title}</div>
              <div className="admin-review-story-meta">
                By {selectedStory.authorName || selectedStory.authorUsername || "Unknown"}
              </div>
              <div className="admin-review-story-meta">
                {selectedStory.forum || "Unclassified"} · {selectedStory.writingMode || "Mode unknown"}
              </div>
              <div className="admin-review-story-stats">
                <span>{selectedStory.sceneCount || 0} scenes</span>
                <span>{selectedStory.canonCount || 0} canon</span>
                <span>Updated {fmtDate(selectedStory.updatedAt)}</span>
              </div>
            </div>
          ) : null}

          <PlatformSuggestionCard
            suggestion={suggestionFrom(selectedStory)}
            adminFinalScore={currentAdminScore100}
            overrideNote={adminOverrideNote}
            compact
          />
        </div>

        <form className="admin-review-panel admin-review-form" onSubmit={submitReview}>
          <div className="admin-review-form-head">
            <div>
              <h2>{editingId ? "Edit internal review" : "Create internal review"}</h2>
              <p>Use editorial judgment. Numbers guide the review; they do not replace judgment.</p>
            </div>
            <div className="admin-review-score-stack">
              <div className="admin-review-score-box">
                <span>Weighted score</span>
                <strong>{weightedScore(scores).toFixed(2)}</strong>
              </div>
              <div className="admin-review-score-box subtle">
                <span>Admin final score</span>
                <strong>{currentAdminScore100}</strong>
              </div>
            </div>
          </div>

          <PlatformSuggestionCard
            suggestion={activePlatformSuggestion}
            adminFinalScore={currentAdminScore100}
            overrideNote={adminOverrideNote}
            compact
          />

          <div className="admin-review-form-grid">
            <label>
              Review period
              <input
                className="ns-input"
                value={reviewPeriodLabel}
                onChange={(e) => setReviewPeriodLabel(e.target.value)}
                placeholder="First review, 30-day review, 90-day review..."
              />
            </label>
            <label>
              Review date
              <input className="ns-input" type="date" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} />
            </label>
            <label>
              Next review date
              <input
                className="ns-input"
                type="date"
                value={nextReviewDate}
                onChange={(e) => setNextReviewDate(e.target.value)}
              />
            </label>
            <label>
              Recommended outcome
              <select className="ns-input" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
                {OUTCOMES.map(([value, label]) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="admin-review-scores">
            {SCORE_KEYS.map(([key, label]) => (
              <label key={key}>
                <span>{label}</span>
                <select
                  value={scores[key] || 3}
                  onChange={(e) => setScores((prev) => ({ ...prev, [key]: Number(e.target.value) }))}
                >
                  {scoreOptions()}
                </select>
              </label>
            ))}
          </div>

          <label className="admin-review-notes-label">
            Admin notes
            <textarea
              className="admin-review-notes"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Private notes: strengths, risks, recommendation, communication history, or next action."
            />
          </label>

          <label className="admin-review-notes-label">
            Admin override note
            <textarea
              className={`admin-review-notes ${overrideRequired ? "needs-note" : ""}`}
              value={adminOverrideNote}
              onChange={(e) => setAdminOverrideNote(e.target.value)}
              placeholder="Required when admin judgment differs materially from the platform suggested score. Explain the reason fairly."
            />
          </label>

          <DecisionJustificationCard
            visibilityDecision={visibilityDecision}
            setVisibilityDecision={setVisibilityDecision}
            rewardConsideration={rewardConsideration}
            setRewardConsideration={setRewardConsideration}
            rewardType={rewardType}
            setRewardType={setRewardType}
            rewardAmountText={rewardAmountText}
            setRewardAmountText={setRewardAmountText}
            decisionJustificationNote={decisionJustificationNote}
            setDecisionJustificationNote={setDecisionJustificationNote}
            decisionNoteRequired={decisionNoteRequired}
          />

          <div className="admin-review-form-actions">
            <button type="submit" className="ns-button ns-button-primary" disabled={busy || !selectedStoryId}>
              {busy ? "Saving…" : editingId ? "Update review" : "Save review"}
            </button>
            <button type="button" className="ns-button ns-button-outline" onClick={() => resetForm(selectedStoryId)} disabled={busy}>
              Clear
            </button>
          </div>
        </form>
      </section>

      <section className="admin-review-history">
        <div className="admin-review-history-head">
          <h2>Review history</h2>
          <p>{selectedStory ? selectedStory.title : "Recent internal reviews"}</p>
        </div>

        {loading ? (
          <div className="admin-review-empty">Loading reviews…</div>
        ) : selectedReviews.length === 0 ? (
          <div className="admin-review-empty">No internal reviews yet for this selection.</div>
        ) : (
          <div className="admin-review-table-wrap">
            <table className="admin-review-table">
              <thead>
                <tr>
                  <th>Review date</th>
                  <th>Storyline</th>
                  <th>Writer</th>
                  <th>Admin score</th>
                  <th>Suggested</th>
                  <th>Override</th>
                  <th>Outcome</th>
                  <th>Decision</th>
                  <th>Next review</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedReviews.map((review) => {
                  const suggestion = suggestionFrom(review);
                  const reviewAdminScore = Math.round(Number(review.weightedScore || 0) * 20);
                  const reviewDiff = scoreDiff(reviewAdminScore, suggestion);
                  const hasOverrideNote = !!safeStr(review.adminOverrideNote).trim();
                  return (
                    <tr key={review.id}>
                      <td>{fmtDate(review.reviewDate)}</td>
                      <td>
                        <strong>{review.storyTitle || "Untitled"}</strong>
                        <div className="admin-review-small">{review.reviewPeriodLabel || "Review"}</div>
                      </td>
                      <td>
                        {review.writerName || review.writerUsername || "Unknown"}
                        {review.writerUsername ? <div className="admin-review-small">@{review.writerUsername}</div> : null}
                      </td>
                      <td>
                        {Number(review.weightedScore || 0).toFixed(2)}
                        <div className="admin-review-small">{reviewAdminScore}/100</div>
                      </td>
                      <td>
                        <strong>{smallNumber(suggestion?.score || review.platformSuggestedScore || 0)}/100</strong>
                        <div className="admin-review-small">{confidenceLabel(suggestion?.confidence || review.platformDataConfidence)}</div>
                      </td>
                      <td>
                        {reviewDiff >= 15 ? (
                          <span className={`admin-review-outcome ${hasOverrideNote ? "steady" : "warn"}`}>
                            {hasOverrideNote ? "Saved" : "Needed"}
                          </span>
                        ) : (
                          <span className="admin-review-outcome neutral">—</span>
                        )}
                      </td>
                      <td>
                        <span className={`admin-review-outcome ${outcomeClass(review.outcome)}`}>
                          {outcomeLabel(review.outcome)}
                        </span>
                      </td>
                      <td>
                        <span className={`admin-review-outcome ${decisionBadgeClass(review.visibilityDecision)}`}>
                          {choiceLabel(VISIBILITY_DECISIONS, review.visibilityDecision, "Observe")}
                        </span>
                        <div className="admin-review-small">
                          Reward: {choiceLabel(REWARD_CONSIDERATIONS, review.rewardConsideration, "Not considered")}
                        </div>
                      </td>
                      <td>{fmtDate(review.nextReviewDate)}</td>
                      <td>
                        <div className="admin-review-row-actions">
                          <button type="button" className="ns-button ns-button-outline" onClick={() => editReview(review)}>
                            Edit
                          </button>
                          <button type="button" className="ns-button ns-button-outline" onClick={() => deleteReview(review)}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
